import React, { useEffect, useState } from "react";
import { api } from "@/api/client.js";
import { SEPAQ_OFFICIAL } from "@/content/sepaqHunt2026.js";
import { MEET_POINT } from "@/content/huntBoard.js";
import { DEPART_EVENTS, DESTINATIONS, clockFace, driveArrival, mapLinks } from "@/lib/nowPlan.js";
import { torontoClock } from "@/lib/huntDates.js";
import { weatherFace, windFr } from "@/lib/weatherFace.js";

const KEY = "orignal2026.destination";
const WATCH = [
  "Heure d’arrivée au poste d’accueil",
  "Travaux et ralentissements — dans Waze",
  "Météo au secteur Desrochers",
  "Heure du chalet — estimation du groupe",
];

function usePhone() {
  const [phone, setPhone] = useState(() => window.matchMedia("(max-width: 899px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px)");
    const apply = () => setPhone(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return phone;
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) / 60));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours <= 0) return `${minutes} min`;
  return `${hours} h ${String(minutes).padStart(2, "0")}`;
}

export function RouteModal({ suggested }) {
  const [choice, setChoice] = useState(() => window.localStorage.getItem(KEY) || suggested || "gatineau");
  const [route, setRoute] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [weather, setWeather] = useState(null);
  const phone = usePhone();
  const place = DESTINATIONS.find((item) => item.id === choice) || DESTINATIONS[0];
  const navId = route?.basis === "position" ? choice : (choice === "retour" || choice === "allard" ? choice : "sepaq");
  const nav = DESTINATIONS.find((item) => item.id === navId) || place;
  const links = mapLinks(nav.query);
  const face = route?.ok && route.to_name === "Accueil Sépaq" && route.basis !== "position" ? driveArrival(route.seconds) : null;
  const steps = DEPART_EVENTS.filter((event) => event.id !== "wake");

  function choose(id) {
    setChoice(id);
    window.localStorage.setItem(KEY, id);
    setRoute(null);
    setTried(false);
  }

  async function loadRoute(origin) {
    const params = new URLSearchParams({ destination: choice });
    if (origin) {
      params.set("origin_lat", String(origin.lat));
      params.set("origin_lng", String(origin.lng));
    }
    const body = await api(`/api/route?${params.toString()}`);
    return body?.ok === false || body?.ok === true ? body : { ok: false, reason: "route_indisponible" };
  }

  function calculate() {
    if (!place.query) {
      setTried(true);
      setRoute({ ok: false, reason: "adresse_non_confirmee" });
      return;
    }
    let alive = true;
    let sawPosition = false;
    setBusy(true);
    setTried(true);
    loadRoute(null).then((body) => {
      if (alive && !sawPosition) setRoute(body);
    }).catch(() => {
      if (alive && !sawPosition) setRoute({ ok: false, reason: "route_indisponible" });
    }).finally(() => {
      if (alive && !sawPosition) setBusy(false);
    });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        sawPosition = true;
        try {
          const body = await loadRoute({ lat: position.coords.latitude, lng: position.coords.longitude });
          if (alive) setRoute(body);
        } catch {
          if (alive) setRoute({ ok: false, reason: "route_indisponible" });
        } finally {
          if (alive) setBusy(false);
        }
      }, () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 120000 });
    }
    return () => { alive = false; };
  }

  useEffect(() => {
    return calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice]);

  useEffect(() => {
    api("/api/weather?day=2026-10-02").then((body) => {
      if (body?.ok) setWeather(body);
    }).catch(() => setWeather(null));
  }, []);

  const hours = (weather?.hourly || []).slice(0, 5);
  const fresh = weather?.fetched_at ? torontoClock(weather.fetched_at) : clockFace(new Date());

  return (
    <div className="og-ride" data-testid="route-modal">
      {phone ? (
        <section className="og-ride__thumb" data-testid="route-thumb">
          <p className="og-ride__where">{route?.ok ? route.to_name : place.label}</p>
          {route?.ok ? (
            <>
              <p className="og-ride__big" data-testid="route-thumb-figures">{formatDuration(route.seconds)} · {Math.round(route.meters / 1000)} km</p>
              {face ? (
                <>
                  <p>Arrivée estimée : {face.arrival}</p>
                  <p>Si départ à 6 h 00</p>
                  <p>RDV Sépaq : 13 h 45</p>
                  <p>Marge : {face.margin}</p>
                </>
              ) : (
                <p>Arrivée estimée : {clockFace(new Date(Date.now() + route.seconds * 1000))}</p>
              )}
              {route.note ? <p>{route.note}</p> : null}
            </>
          ) : (
            <>
              <p className="og-ride__big">{busy ? "Calcul…" : "—"}</p>
              <p>RDV Sépaq : 13 h 45</p>
              <p>{tried && !busy ? "Calcul routier indisponible après deux essais." : "Calcul du trajet…"}</p>
            </>
          )}
          <div className="og-route-actions og-ride__actions">
            {links ? <a className="og-btn og-btn--primary" data-testid="route-waze" href={links.waze} target="_blank" rel="noreferrer">Ouvrir dans Waze</a> : <p>Waze : adresse non confirmée.</p>}
            {links ? <a className="og-btn og-btn--primary" data-testid="route-google" href={links.google} target="_blank" rel="noreferrer">Ouvrir dans Google Maps</a> : null}
          </div>
        </section>
      ) : null}
      {phone ? <p className="og-ride__more">Voir le détail du trajet</p> : null}
      <header className="og-ride__hero">
        <div>
          <p className="og-ride__kicker">On sait où on va, quand on part, et ce qui nous attend.</p>
        </div>
        <p className="og-ride__tag">Même route. Plus de moments en nature.</p>
      </header>

      <section className="og-ride__card">
        <h3>Résumé du trajet</h3>
        <div className="og-ride__summary">
          <ol className="og-ride__chain">
            <li>
              <span>Départ</span>
              <strong>{MEET_POINT}</strong>
              <em>Calcul depuis ma position actuelle</em>
            </li>
            <li>
              <span>Étape 1</span>
              <strong>Accueil Sépaq</strong>
              <em>{SEPAQ_OFFICIAL.street}, {SEPAQ_OFFICIAL.city}</em>
            </li>
            <li>
              <span>Étape 2</span>
              <strong>Chalet Desrochers</strong>
              <em>Adresse du chalet non confirmée</em>
            </li>
          </ol>
          <div className="og-ride__facts">
            <article>
              <span>Arrivée prévue</span>
              <strong>entre 12 h et 13 h</strong>
              <em>au poste d’accueil</em>
            </article>
            <article>
              <span>Rendez-vous Sépaq</span>
              <strong>13 h 45</strong>
              <em>enregistrement</em>
            </article>
            <article>
              <span>Vers le chalet</span>
              <strong>environ 1 h 15</strong>
              <em>après Sépaq · estimation du groupe</em>
            </article>
          </div>
        </div>
      </section>

      <div className="og-ride__mid">
        <section className="og-ride__card">
          <h3>Comparaison</h3>
          <div className="og-ride__compare">
            <article>
              <span>Plan du groupe</span>
              <strong>12 h–13 h</strong>
              <em>Arrivée à l’accueil. Distance non notée.</em>
              <em>Puis environ 1 h 15 jusqu’au chalet.</em>
            </article>
            <article data-testid="route-result">
              <span>{route?.basis === "position" ? "Depuis ta position" : "Trajet calculé"}</span>
              {route?.ok ? (
                <>
                  <strong>{formatDuration(route.seconds)}</strong>
                  <em>{Math.round(route.meters / 1000)} km · {route.from_name} → {route.to_name}</em>
                  <em>{route.note || "Calcul routier."}</em>
                </>
              ) : (
                <>
                  <strong>{busy ? "Calcul…" : "—"}</strong>
                  <em>{tried && !busy ? "Calcul routier indisponible après deux essais." : "Calcul du trajet…"}</em>
                  <em>{place.query ? "Ouvrir Waze ou Google Maps." : "Adresse non confirmée."}</em>
                </>
              )}
            </article>
            <article>
              <span>Waze</span>
              <strong>En direct</strong>
              <em>Le trafic se lit dans Waze, pas ici.</em>
            </article>
          </div>
        </section>

        <section className="og-ride__card">
          <h3>Météo au secteur</h3>
          <p>Desrochers · lecture {fresh}. Pas le long de l’autoroute.</p>
          {hours.length ? (
            <ul className="og-ride__hours">
              {hours.map((hour) => {
                const face = weatherFace(hour.weather_code, Number(String(hour.hour).slice(0, 2)) >= 18);
                return (
                  <li key={hour.hour}>
                    <strong>{Number(String(hour.hour).slice(0, 2))} h</strong>
                    <span>{face.icon} {hour.temperature ?? "—"} °C</span>
                    <em>{face.label}</em>
                    <em>{windFr(hour.wind_dir)} {hour.wind ?? "—"}</em>
                  </li>
                );
              })}
            </ul>
          ) : <p>Météo indisponible pour le moment.</p>}
          <ul className="og-ride__road">
            <li><strong>Routes</strong><span>Non vérifiées ici</span></li>
            <li><strong>Travaux</strong><span>Non vérifiés ici</span></li>
            <li><strong>Fermetures</strong><span>Non vérifiées ici</span></li>
          </ul>
        </section>
      </div>

      <div className="og-ride__low">
        <section className="og-ride__card">
          <h3>Étapes du trajet</h3>
          <ol className="og-ride__time">
            {steps.map((event) => (
              <li key={event.id}>
                <strong>{event.id === "cabin" ? "+ 1 h 15" : event.id === "region" ? "12 h–13 h" : event.when}</strong>
                <span>{event.step}{event.note ? ` · ${event.note}` : ""}</span>
              </li>
            ))}
          </ol>
        </section>
        <section className="og-ride__card og-ride__watch">
          <h3>Ce qu’on surveille</h3>
          <ul>
            {WATCH.map((line) => <li key={line}>{line}</li>)}
          </ul>
          <p className="og-ride__quote">Bien planifier pour mieux chasser.</p>
        </section>
        <section className="og-ride__card">
          <h3>Destination rapide</h3>
          <div className="og-ride__picks" data-testid="route-destinations">
            {DESTINATIONS.map((item) => (
              <button key={item.id} type="button" className={choice === item.id ? "is-on" : ""} onClick={() => choose(item.id)}>
                {item.id === "gatineau" ? "Station Gatineau" : item.label}
              </button>
            ))}
          </div>
          {place.note ? <p>{place.note}</p> : null}
          <h3>Actions</h3>
          <div className="og-route-actions og-ride__actions">
            {!phone && links ? <a className="og-btn og-btn--primary" data-testid="route-waze" href={links.waze} target="_blank" rel="noreferrer">Ouvrir dans Waze</a> : null}
            {!phone && links ? <a className="og-btn og-btn--primary" data-testid="route-google" href={links.google} target="_blank" rel="noreferrer">Ouvrir dans Google Maps</a> : null}
            {!phone && !links ? <p>Waze : adresse non confirmée.</p> : null}
            <button type="button" className="og-btn" data-testid="route-calculate" onClick={calculate} disabled={busy}>Recalculer maintenant</button>
          </div>
        </section>
      </div>
    </div>
  );
}
